import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import C from '../../data/colors';
import { Mail, MessageSquare, Search, Trash2, Archive, CheckSquare, Square, AlertTriangle, Check, Send, CheckCircle, XCircle } from 'lucide-react';
import AdminPageHeader from './AdminPageHeader';

const API = process.env.REACT_APP_BACKEND_URL;

const SentHistoryTab = () => {
  const [mode, setMode] = useState('email'); // email | sms
  const [emailLogs, setEmailLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date | name | status
  const [sortDirection, setSortDirection] = useState('desc'); // asc | desc
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState(null);

  // Bulk delete modal state
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Load logs
  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  // Clear selection when mode or search changes
  useEffect(() => {
    setSelected([]);
  }, [mode, search]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/api/email-logs`),
        axios.get(`${API}/api/sms-logs`)
      ]);
      setEmailLogs(emailRes.data.filter(l => !l.archived));
      setSmsLogs(smsRes.data);
    } catch (e) {
      console.error('Error loading logs:', e);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  };

  const currentLogs = mode === 'email' ? emailLogs : smsLogs;

  // Status priority for sorting (higher priority = more important)
  const STATUS_PRIORITY = {
    'failed': 1,
    'bounced': 2,
    'dropped': 3,
    'spam': 4,
    'spamreport': 4,
    'deferred': 5,
    'delivered': 6,
    'sent': 7,
    'processed': 8,
    'opened': 9,
    'clicked': 10,
    'unsubscribed': 11
  };

  // Handle sort button click - toggle direction if same column, reset if different
  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - set default direction
      setSortBy(newSortBy);
      setSortDirection(newSortBy === 'date' ? 'desc' : 'asc'); // Date defaults to newest first
    }
  };

  // Filter and sort with proper dependencies
  const filteredLogs = useMemo(() => {
    let logs = [...currentLogs];

    // Search filter
    if (search) {
      const s = search.toLowerCase();
      logs = logs.filter(l =>
        (l.guest_name || l.recipient_name || '').toLowerCase().includes(s) ||
        (l.guest_email || l.phone_number || '').toLowerCase().includes(s) ||
        (l.template_name || '').toLowerCase().includes(s) ||
        (l.status || '').toLowerCase().includes(s)
      );
    }

    // Sort with direction support
    logs.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'date') {
        const dateA = new Date(a.sent_at || 0).getTime();
        const dateB = new Date(b.sent_at || 0).getTime();
        comparison = dateA - dateB;
      } else if (sortBy === 'name') {
        const nameA = (a.guest_name || a.recipient_name || a.guest_email || a.phone_number || '').toLowerCase();
        const nameB = (b.guest_name || b.recipient_name || b.guest_email || b.phone_number || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === 'status') {
        const statusA = (a.status || '').toLowerCase();
        const statusB = (b.status || '').toLowerCase();
        const priorityA = STATUS_PRIORITY[statusA] || 99;
        const priorityB = STATUS_PRIORITY[statusB] || 99;
        comparison = priorityA - priorityB;
      }
      
      // Apply direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return logs;
  }, [currentLogs, search, sortBy, sortDirection]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(filteredLogs.map(l => l.id));
  };

  const deselectAll = () => {
    setSelected([]);
  };

  const handleArchive = async (logId) => {
    try {
      await axios.patch(`${API}/api/email-logs/${logId}/archive`);
      loadLogs();
      setMessage({ type: 'success', text: 'Log archived' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Error archiving log' });
    }
  };

  const handleSingleDelete = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this log? This action cannot be undone.')) return;

    try {
      if (mode === 'email') {
        await axios.delete(`${API}/api/email-logs/${logId}`);
      } else {
        await axios.delete(`${API}/api/sms-logs/${logId}`);
      }
      loadLogs();
      setSelected(prev => prev.filter(id => id !== logId));
      setMessage({ type: 'success', text: 'Log deleted (audit trail preserved)' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Error deleting log' });
    }
  };

  const handleBulkDelete = async () => {
    if (deleteConfirmText !== 'DELETE') return;

    setDeleting(true);
    try {
      if (mode === 'email') {
        await axios.post(`${API}/api/email-logs/bulk-delete`, {
          log_ids: selected,
          confirmation: 'DELETE'
        });
      } else {
        await axios.post(`${API}/api/sms-logs/bulk-delete`, {
          log_ids: selected,
          confirmation: 'DELETE'
        });
      }
      
      setMessage({ type: 'success', text: `Deleted ${selected.length} log(s) (audit trail preserved)` });
      setSelected([]);
      setShowBulkDelete(false);
      setDeleteConfirmText('');
      loadLogs();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Error deleting logs' });
    }
    setDeleting(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent':
      case 'delivered':
      case 'processed':
        return '#10B981';
      case 'failed':
      case 'bounced':
      case 'dropped':
        return '#EF4444';
      case 'opened':
      case 'clicked':
        return '#3B82F6';
      case 'spam':
      case 'spamreport':
        return '#F97316';
      case 'deferred':
        return '#F59E0B';
      case 'unsubscribed':
        return '#6B7280';
      default:
        return C.grey;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const selectedLogs = filteredLogs.filter(l => selected.includes(l.id));

  return (
    <div data-testid="sent-history-tab">
      <AdminPageHeader
        eyebrow="Messaging"
        title="Sent History"
        tagline="Delivery log for every email and SMS you've sent."
      />

      {/* Summary Metrics - Section 7 Requirement */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: 16, 
          marginBottom: 24,
          padding: 16,
          background: C.bgCard,
          borderRadius: 12,
          border: `1px solid ${C.border}`,
        }}>
          <SummaryMetric 
            icon={<CheckCircle size={18} color="#22c55e" />} 
            label="Emails Sent" 
            value={stats.emails_sent_this_month || 0}
            subtext="This month"
          />
          <SummaryMetric 
            icon={<XCircle size={18} color="#ff6b6b" />} 
            label="Failed" 
            value={stats.emails_failed || 0}
            color="#ff6b6b"
          />
          <SummaryMetric 
            icon={<Mail size={18} color="#60a5fa" />} 
            label="Opened" 
            value={stats.emails_opened || 0}
          />
          <SummaryMetric 
            icon={<Send size={18} color={C.gold} />} 
            label="SMS Sent" 
            value={stats.sms_sent || 0}
          />
        </div>
      )}

      {/* Mode Toggle - REQUIREMENT 4: Count must match filtered dataset */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          data-testid="history-mode-email"
          onClick={() => setMode('email')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8,
            border: mode === 'email' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
            background: mode === 'email' ? C.goldMuted : C.bgCard,
            color: C.textPrimary, cursor: 'pointer', fontFamily: 'Cinzel, serif'
          }}
        >
          <Mail size={18} /> Email ({mode === 'email' ? filteredLogs.length : emailLogs.length})
          {mode === 'email' && search && filteredLogs.length !== emailLogs.length && (
            <span style={{ fontSize: 11, color: C.textSecondary }}> of {emailLogs.length}</span>
          )}
        </button>
        <button
          data-testid="history-mode-sms"
          onClick={() => setMode('sms')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8,
            border: mode === 'sms' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
            background: mode === 'sms' ? C.goldMuted : C.bgCard,
            color: C.textPrimary, cursor: 'pointer', fontFamily: 'Cinzel, serif'
          }}
        >
          <MessageSquare size={18} /> SMS ({mode === 'sms' ? filteredLogs.length : smsLogs.length})
          {mode === 'sms' && search && filteredLogs.length !== smsLogs.length && (
            <span style={{ fontSize: 11, color: C.textSecondary }}> of {smsLogs.length}</span>
          )}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(220,53,69,0.15)',
          color: message.type === 'success' ? '#22c55e' : '#ff6b6b',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          {message.text}
          <button 
            onClick={() => setMessage(null)} 
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.textSecondary }} />
          <input
            data-testid="history-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search logs..."
            style={{
              width: '100%', padding: '10px 12px 10px 36px', borderRadius: 8,
              border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box',
              background: C.bgInput, color: C.textPrimary
            }}
          />
        </div>

        {/* Sort */}
        <div style={{ display: 'flex', gap: 4 }}>
          {['date', 'name', 'status'].map(s => {
            const isActive = sortBy === s;
            const arrow = isActive 
              ? (sortDirection === 'asc' ? '↑' : '↓')
              : '';
            return (
              <button
                key={s}
                data-testid={`sort-${s}`}
                onClick={() => handleSort(s)}
                style={{
                  padding: '8px 12px', borderRadius: 6, fontSize: 12,
                  border: isActive ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                  background: isActive ? C.gold : C.bgCard,
                  color: isActive ? '#000' : C.textPrimary,
                  cursor: 'pointer', textTransform: 'capitalize',
                  fontWeight: isActive ? 600 : 400,
                  boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  minWidth: 70
                }}
              >
                {s} {arrow}
              </button>
            );
          })}
        </div>

        {/* Select All */}
        <button
          onClick={selected.length === filteredLogs.length ? deselectAll : selectAll}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            border: `1px solid ${C.border}`, background: C.bgCard,
            cursor: 'pointer', color: C.textPrimary
          }}
        >
          {selected.length === filteredLogs.length && filteredLogs.length > 0 ? (
            <><CheckSquare size={14} /> Deselect All</>
          ) : (
            <><Square size={14} /> Select All</>
          )}
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selected.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px', marginBottom: 16,
          background: C.goldMuted, borderRadius: 8, border: `1px solid ${C.gold}`
        }}>
          <span style={{ fontSize: 14, color: C.textPrimary, fontWeight: 500 }}>
            Selected: {selected.length}
          </span>

          <button
            onClick={deselectAll}
            style={{
              fontSize: 12, color: C.textSecondary, background: 'none',
              border: 'none', cursor: 'pointer', textDecoration: 'underline'
            }}
          >
            Clear
          </button>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {mode === 'email' && (
              <button
                data-testid="bulk-archive-btn"
                onClick={async () => {
                  for (const id of selected) {
                    await handleArchive(id);
                  }
                  setSelected([]);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 6,
                  border: `1px solid ${C.border}`, background: C.bgCard,
                  cursor: 'pointer', fontSize: 13, color: C.textPrimary
                }}
              >
                <Archive size={14} /> Archive Selected
              </button>
            )}

            <button
              data-testid="bulk-delete-btn"
              onClick={() => setShowBulkDelete(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 6,
                border: 'none', background: '#EF4444',
                cursor: 'pointer', fontSize: 13, color: '#fff'
              }}
            >
              <Trash2 size={14} /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Logs List - REQUIREMENT 5: Full width on desktop */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.textSecondary }}>Loading...</div>
      ) : filteredLogs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.textSecondary }}>
          {mode === 'email' ? <Mail size={48} style={{ opacity: 0.3, marginBottom: 12 }} /> : <MessageSquare size={48} style={{ opacity: 0.3, marginBottom: 12 }} />}
          <p>No {mode} logs found</p>
        </div>
      ) : (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 8,
          width: '100%',  /* Full width */
          maxWidth: '100%',
          overflowX: 'auto'  /* Allow scroll on mobile only */
        }}>
          {/* Desktop Table Header - shows on screens > 768px */}
          <div style={{
            display: 'none',
            gridTemplateColumns: '40px 1fr 150px 120px 180px 100px',
            gap: 12,
            padding: '10px 16px',
            background: C.bgPrimary,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 12,
            color: C.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }} className="desktop-table-header">
            <span></span>
            <span>Recipient</span>
            <span>Template</span>
            <span>Status</span>
            <span>Sent</span>
            <span>Actions</span>
          </div>
          
          {filteredLogs.map(log => (
            <div
              key={log.id}
              data-testid={`log-row-${log.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth >= 768 
                  ? '40px 1fr 150px 120px 180px 100px' 
                  : '40px 1fr auto',
                alignItems: 'center',
                padding: '12px 16px',
                background: selected.includes(log.id) ? C.goldMuted : C.bgCard,
                border: `1px solid ${selected.includes(log.id) ? C.gold : C.border}`,
                borderRadius: 8,
                gap: 12,
                width: '100%',
                minWidth: window.innerWidth >= 768 ? '800px' : 'auto'
              }}
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selected.includes(log.id)}
                onChange={() => toggleSelect(log.id)}
                style={{ width: 16, height: 16, accentColor: C.gold }}
              />

              {/* Log Info - Recipient */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500, color: C.textPrimary, fontSize: 14 }}>
                    {log.guest_name || 'Unknown'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>
                  {mode === 'email' ? log.guest_email : log.phone_number}
                </div>
                {mode === 'email' && log.subject && (
                  <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    "{log.subject}"
                  </div>
                )}
              </div>
              
              {/* Template - Desktop only */}
              <div style={{ 
                fontSize: 12, 
                color: C.textSecondary,
                display: window.innerWidth >= 768 ? 'block' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {log.template_name || 'Unknown'}
              </div>
              
              {/* Status */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 4,
                  background: `${getStatusColor(log.status)}20`,
                  color: getStatusColor(log.status),
                  textTransform: 'uppercase', fontWeight: 600,
                  width: 'fit-content'
                }}>
                  {log.status}
                </span>
                {log.opened && (
                  <span style={{ fontSize: 9, color: '#60a5fa' }}>
                    ✓ Opened{log.open_count > 1 ? ` (${log.open_count}x)` : ''}
                  </span>
                )}
              </div>
              
              {/* Sent Date - Desktop only */}
              <div style={{ 
                fontSize: 12, 
                color: C.textSecondary,
                display: window.innerWidth >= 768 ? 'block' : 'none'
              }}>
                {formatDate(log.sent_at || log.created_at)}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 4 }}>
                {mode === 'email' && (
                  <button
                    data-testid={`archive-btn-${log.id}`}
                    onClick={() => handleArchive(log.id)}
                    title="Archive"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, padding: 4 }}
                  >
                    <Archive size={16} />
                  </button>
                )}
                <button
                  data-testid={`delete-btn-${log.id}`}
                  onClick={() => handleSingleDelete(log.id)}
                  title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: C.bgCard, borderRadius: 12, padding: 24,
            maxWidth: 500, width: '90%', border: `1px solid ${C.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <AlertTriangle size={24} color="#EF4444" />
              <h3 style={{ margin: 0, fontFamily: 'Cinzel, serif', color: C.textPrimary }}>
                Confirm Bulk Delete
              </h3>
            </div>

            <p style={{ fontSize: 14, color: C.textPrimary, marginBottom: 8 }}>
              You are about to delete <strong>{selected.length} {mode} log(s)</strong>.
            </p>

            <div style={{
              background: 'rgba(245,158,11,0.15)', padding: 12, borderRadius: 8,
              marginBottom: 16, fontSize: 13, color: '#f59e0b'
            }}>
              <strong>Note:</strong> An immutable audit record will be preserved for compliance.
            </div>
            
            <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 16 }}>
              Recipients: {selectedLogs.slice(0, 3).map(l => l.guest_name || l.guest_email || l.phone_number).join(', ')}
              {selectedLogs.length > 3 && ` +${selectedLogs.length - 3} more...`}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.textPrimary, display: 'block', marginBottom: 8 }}>
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                data-testid="bulk-delete-confirm-input"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box',
                  background: C.bgInput, color: C.textPrimary
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBulkDelete(false); setDeleteConfirmText(''); }}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.bgPrimary,
                  cursor: 'pointer', fontSize: 14, color: C.textPrimary
                }}
              >
                Cancel
              </button>
              <button
                data-testid="bulk-delete-confirm-btn"
                onClick={handleBulkDelete}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: 'none',
                  background: deleteConfirmText === 'DELETE' ? '#EF4444' : C.bgPrimary,
                  color: deleteConfirmText === 'DELETE' ? '#fff' : C.textSecondary,
                  cursor: deleteConfirmText === 'DELETE' && !deleting ? 'pointer' : 'not-allowed',
                  fontSize: 14, fontFamily: 'Cinzel, serif'
                }}
              >
                {deleting ? 'Deleting...' : `Delete ${selected.length} Log(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary Metric Component for Section 7
const SummaryMetric = ({ icon, label, value, color, subtext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{
      width: 40,
      height: 40,
      borderRadius: 10,
      background: C.bgPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || C.textPrimary }}>{value}</div>
      <div style={{ fontSize: 12, color: C.textSecondary }}>{label}</div>
      {subtext && <div style={{ fontSize: 10, color: C.textSecondary, marginTop: 1 }}>{subtext}</div>}
    </div>
  </div>
);

export default SentHistoryTab;
